import { useCallback, useEffect, useState } from "react";
import { History, Plus } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { SCH } from "@/constants/testIds";
import {
  SCH_STATUSES,
  createSystemChange,
  listSystemChanges,
  updateSystemChangeStatus,
} from "@/lib/systemChangeHistoryApi";

const BLANK = {
  title: "",
  summary: "",
  gitSha: "",
  gitTag: "",
  status: "recorded",
};

function statusLabel(id) {
  return SCH_STATUSES.find((row) => row.id === id)?.label || id;
}

function formatWhen(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export default function SystemChangeHistoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSystemChanges();
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load System Change History.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await createSystemChange({
        title: form.title.trim(),
        summary: form.summary.trim(),
        gitSha: form.gitSha.trim(),
        gitTag: form.gitTag.trim() || undefined,
        status: form.status,
      });
      toast.success("Change recorded.");
      setFormOpen(false);
      setForm(BLANK);
      await load();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Unable to record change.");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await updateSystemChangeStatus(id, status);
      await load();
    } catch {
      toast.error("Unable to update status.");
    }
  };

  return (
    <div className="space-y-6" data-testid={SCH.page}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-page-title font-semibold text-slate-900" data-testid={SCH.title}>
            System Change History
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Admin ledger of meaningful EMRbyMD milestones. Not a Git commit log and not
            patient clinical history.
          </p>
        </div>
        <Button
          type="button"
          data-testid={SCH.addBtn}
          onClick={() => {
            setForm(BLANK);
            setFormOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Record change
        </Button>
      </div>

      <div
        data-testid={SCH.banner}
        className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        Record a row after a baseline, TEST deploy, or production deploy. Do not add a row
        for every commit.
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table data-testid={SCH.table}>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Git</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Recorded by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-sm text-slate-500">
                  Loading…
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-sm text-slate-500" data-testid={SCH.empty}>
                  No system changes recorded yet.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} data-testid={`${SCH.row}-${item.id}`}>
                  <TableCell className="whitespace-nowrap text-sm text-slate-600">
                    {formatWhen(item.changedAt)}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-slate-900">{item.title}</div>
                    {item.summary ? (
                      <p className="mt-1 max-w-xl text-sm text-slate-500">{item.summary}</p>
                    ) : null}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">
                    <div>{item.gitSha}</div>
                    {item.gitTag ? <div className="mt-1 text-slate-500">{item.gitTag}</div> : null}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-2">
                      <Badge variant="secondary">{statusLabel(item.status)}</Badge>
                      <Select value={item.status} onValueChange={(value) => setStatus(item.id, value)}>
                        <SelectTrigger className="h-8 w-[10rem] text-xs" aria-label="Update status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SCH_STATUSES.map((row) => (
                            <SelectItem key={row.id} value={row.id}>
                              {row.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {item.recordedBy?.displayName || "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Record a system change
            </DialogTitle>
            <DialogDescription>
              Use this for milestones such as a new main baseline or an environment deploy.
            </DialogDescription>
          </DialogHeader>
          <form data-testid={SCH.form} className="space-y-4" onSubmit={save}>
            <div className="space-y-1.5">
              <Label htmlFor={SCH.formTitle}>Title</Label>
              <Input
                id={SCH.formTitle}
                data-testid={SCH.formTitle}
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={SCH.formSummary}>Summary</Label>
              <Textarea
                id={SCH.formSummary}
                data-testid={SCH.formSummary}
                value={form.summary}
                onChange={(event) => setForm((current) => ({ ...current, summary: event.target.value }))}
                rows={4}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={SCH.formSha}>Git SHA</Label>
              <Input
                id={SCH.formSha}
                data-testid={SCH.formSha}
                className="font-mono"
                value={form.gitSha}
                onChange={(event) => setForm((current) => ({ ...current, gitSha: event.target.value }))}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={SCH.formTag}>Git tag (optional)</Label>
              <Input
                id={SCH.formTag}
                data-testid={SCH.formTag}
                className="font-mono"
                value={form.gitTag}
                onChange={(event) => setForm((current) => ({ ...current, gitTag: event.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(value) => setForm((current) => ({ ...current, status: value }))}
              >
                <SelectTrigger data-testid={SCH.formStatus}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCH_STATUSES.map((row) => (
                    <SelectItem key={row.id} value={row.id}>
                      {row.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                data-testid={SCH.formCancel}
                onClick={() => setFormOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid={SCH.formSave} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
